import { Box, Table, Link, Text, Skeleton, HStack } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom'
import { ResponsiveTable, type ResponsiveColumn } from '../shared'
import type { ContactSearchResult, PaginatedResponse } from '../../types/contact'

interface ContactSearchResultsProps {
  data?: PaginatedResponse<ContactSearchResult>
  isLoading?: boolean
}

export function ContactSearchResults({ data, isLoading }: ContactSearchResultsProps) {
  if (isLoading) {
    return (
      <Box bg="surface" borderRadius="lg" shadow="md" overflow="hidden">
        <Table.Root size="sm">
          <Table.Header bg="gray.50">
            <Table.Row>
              <Table.ColumnHeader>Name</Table.ColumnHeader>
              <Table.ColumnHeader textAlign='end'>Age</Table.ColumnHeader>
              <Table.ColumnHeader>Phone</Table.ColumnHeader>
              <Table.ColumnHeader>Email</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {[...Array(5)].map((_, i) => (
              <Table.Row key={i}>
                <Table.Cell><Skeleton height="16px" width="180px" /></Table.Cell>
                <Table.Cell><Skeleton height="16px" width="30px" /></Table.Cell>
                <Table.Cell><Skeleton height="16px" width="100px" /></Table.Cell>
                <Table.Cell><Skeleton height="16px" width="150px" /></Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Box>
    );
  }

  if (!data || data.items.length === 0) {
    return (
      <Box bg="surface" p={8} borderRadius="lg" shadow="md" textAlign="center">
        <Text color="gray.500">No contacts found. Try adjusting your search criteria.</Text>
      </Box>
    )
  }

  const columns: ResponsiveColumn<ContactSearchResult>[] = [
    {
      header: 'Name',
      mobileRole: 'title',
      cell: (contact) => (
        <HStack gap={2}>
          <Link color="brand.600" fontWeight="medium" asChild><RouterLink to={`/dashboard/contacts/${contact.id}`}>
              {contact.lastName}, {contact.firstName} {contact.middleName ? contact.middleName.charAt(0) : ''}
            </RouterLink></Link>
          {contact.isDeceased && (
            <Text as="span" fontSize="xs" color="gray.500">
              (Deceased)
            </Text>
          )}
        </HStack>
      ),
    },
    {
      header: 'Age',
      mobileRole: 'meta',
      isNumeric: true,
      cell: (contact) => contact.age ?? '',
    },
    {
      header: 'Phone',
      mobileRole: 'meta',
      cell: (contact) => formatPhone(contact.phone),
    },
    {
      header: 'Email',
      mobileRole: 'meta',
      cell: (contact) => contact.email ?? '',
    },
  ]

  return (
    <ResponsiveTable
      columns={columns}
      rows={data.items}
      keyFn={(contact) => contact.id}
    />
  )
}

function formatPhone(phone: string | undefined): string {
  if (!phone) return ''
  // Format as (XXX) XXX-XXXX if it's a 10-digit number
  const digits = phone.replace(/\D/g, '')
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return phone
}
